import { useState, useCallback, useRef, ReactNode } from 'react';
import { IMessageModalCloseButton, MessageModalButtonProps, MessageModalProps } from '.';

const InitialModalButton: IMessageModalCloseButton = {
  kind: 'close',
  cancelLabel: '閉じる',
  onCancel: () => {},
};

/**
 * showModalで確定したかキャンセルしたかを待機できる関数とModal用のPropsを返す
 */
export const useMessageModal = (): {
  modalProps: MessageModalProps;
  showConfirmModal: (message: string, submitLabel?: string, cancelLabel?: string) => Promise<boolean>;
  showMessageModal: (
    message: string | ReactNode,
    onClosed?: () => void,
    cancelLabel?: string,
    cancelButtonClassName?: string,
  ) => void;
  showMessageModalWithoutButton: (message: ReactNode, onClosed?: () => void) => void;
} => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string | ReactNode>('');
  const [button, setButton] = useState<MessageModalButtonProps>(InitialModalButton);

  /**
   * onClickが押された場合にtrueを返す onCancelの場合はfalseを返す
   */
  const showConfirmModal = useCallback(async (message: string, submitLabel?: string, cancelLabel?: string) => {
    return new Promise<boolean>((resolve) => {
      setMessage(message);
      setButton({
        kind: 'confirm',
        submitLabel: submitLabel || 'はい',
        onSubmit: () => {
          setIsOpen(false);
          resolve(true);
        },
        cancelLabel: cancelLabel || 'いいえ',
        onCancel: () => {
          setIsOpen(false);
          resolve(false);
        },
      });
      setIsOpen(true);
    });
  }, []);

  const showMessageModal = useCallback(
    (message: string | ReactNode, onClosed?: () => void, cancelLabel?: string, cancelButtonClassName?: string) => {
      return new Promise<boolean>((resolve) => {
        setMessage(message);
        setButton({
          kind: 'close',
          cancelLabel: cancelLabel || '閉じる',
          cancelButtonClassName: cancelButtonClassName,
          onCancel: () => {
            setIsOpen(false);
            resolve(true);
            onClosed?.();
          },
        });
        setIsOpen(true);
      });
    },
    [],
  );
  const showMessageModalWithoutButton = useCallback((message: ReactNode | string, onClosed?: () => void) => {
    return new Promise<boolean>((resolve) => {
      setMessage(message);
      setButton({
        kind: 'none',
        onCancel: () => {
          setIsOpen(false);
          resolve(true);
          onClosed?.();
        },
      });
      setIsOpen(true);
    });
  }, []);

  return {
    modalProps: {
      message,
      isOpen,
      setIsOpen,
      button,
    },
    showConfirmModal,
    showMessageModal,
    showMessageModalWithoutButton,
  };
};
